import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  updateNarrative,
  narrativeAiAction,
  regenerateSummary,
  reanalyzePackage,
} from '../../utils/financialAnalystApi';
import './FinancialAnalyst.css';

const ExecutiveSummaryPanel = ({ narrative, packageId, isLocked, departmentNotes, onUpdate }) => {
  const [content, setContent] = useState(narrative?.content || '');
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('saved'); // saved | saving | error
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [reanalyzeProgress, setReanalyzeProgress] = useState(null);
  const [aiActionLoading, setAiActionLoading] = useState(null);
  const [showDeptNotes, setShowDeptNotes] = useState(false);
  const saveTimer = useRef(null);
  const contentRef = useRef(null);

  // Sync content when narrative changes externally
  useEffect(() => {
    setContent(narrative?.content || '');
  }, [narrative?.content, narrative?.version]);

  // Cleanup timer on unmount
  useEffect(() => () => clearTimeout(saveTimer.current), []);

  // Debounced auto-save
  const debouncedSave = useCallback((text) => {
    if (!narrative?.id || isLocked) return;
    clearTimeout(saveTimer.current);
    setSaveStatus('saving');
    saveTimer.current = setTimeout(async () => {
      try {
        await updateNarrative(narrative.id, { content: text });
        setSaveStatus('saved');
        if (onUpdate) onUpdate();
      } catch (err) {
        console.error('Auto-save failed:', err);
        setSaveStatus('error');
      }
    }, 1000);
  }, [narrative?.id, isLocked, onUpdate]);

  const handleContentChange = (e) => {
    const text = e.target.value;
    setContent(text);
    debouncedSave(text);
  };

  const handleGenerateSummary = async () => {
    setGeneratingSummary(true);
    try {
      await regenerateSummary(packageId);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Generate summary failed:', err);
    }
    setGeneratingSummary(false);
  };

  const handleReanalyze = async () => {
    setReanalyzing(true);
    setReanalyzeProgress({ message: 'Starting re-analysis...', progress: 0 });
    try {
      await reanalyzePackage(packageId, (event) => {
        if (event.type === 'progress') {
          setReanalyzeProgress(event);
        } else if (event.type === 'complete') {
          setReanalyzing(false);
          setReanalyzeProgress(null);
          if (onUpdate) onUpdate();
        } else if (event.type === 'error') {
          setReanalyzing(false);
          setReanalyzeProgress(null);
        }
      });
    } catch (err) {
      console.error('Re-analysis failed:', err);
      setReanalyzing(false);
      setReanalyzeProgress(null);
    }
  };

  const handleAiAction = async (action) => {
    if (!narrative?.id) return;
    setAiActionLoading(action);
    try {
      await narrativeAiAction(narrative.id, { action });
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error(`AI action ${action} failed:`, err);
    }
    setAiActionLoading(null);
  };

  // Count dept notes
  const deptNoteEntries = Object.entries(departmentNotes || {}).filter(([, notes]) => notes.length > 0);
  const totalDeptNotes = deptNoteEntries.reduce((sum, [, notes]) => sum + notes.length, 0);

  return (
    <div className="fa-exec-panel">
      <div className="fa-exec-panel-header">
        <h3 className="fa-exec-panel-title">Executive Summary</h3>
        {narrative && (
          <span className={`fa-save-indicator ${saveStatus}`}>
            {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'error' ? 'Save failed' : 'Saved'}
          </span>
        )}
      </div>

      {/* Editable content area */}
      <div className="fa-exec-content-area">
        {narrative ? (
          <textarea
            ref={contentRef}
            className="fa-exec-content-textarea"
            value={content}
            onChange={handleContentChange}
            readOnly={isLocked}
            placeholder="Write your executive summary..."
          />
        ) : (
          <div className="fa-exec-empty">
            No executive summary yet. Click "Generate AI Summary" to create one, or start typing.
          </div>
        )}
      </div>

      {/* AI Action Buttons */}
      {!isLocked && (
        <div className="fa-exec-actions">
          <div className="fa-exec-actions-primary">
            <button
              className="fa-btn fa-btn-sm fa-btn-primary"
              onClick={handleGenerateSummary}
              disabled={generatingSummary || reanalyzing}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
              {generatingSummary ? 'Generating...' : 'Generate AI Summary'}
            </button>
            <button
              className="fa-btn fa-btn-sm fa-btn-accent"
              onClick={handleReanalyze}
              disabled={reanalyzing || generatingSummary}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
              {reanalyzing ? 'Re-analyzing...' : 'Full Re-Analysis'}
            </button>
          </div>

          {reanalyzing && reanalyzeProgress && (
            <div className="fa-exec-progress">
              <div className="fa-exec-progress-bar">
                <div className="fa-exec-progress-fill" style={{ width: `${reanalyzeProgress.progress || 0}%` }} />
              </div>
              <span className="fa-exec-progress-text">{reanalyzeProgress.message}</span>
            </div>
          )}

          {narrative && (
            <div className="fa-exec-actions-secondary">
              <button className="fa-btn fa-btn-sm" onClick={() => handleAiAction('polish')} disabled={!!aiActionLoading}>
                {aiActionLoading === 'polish' ? '...' : 'Polish'}
              </button>
              <button className="fa-btn fa-btn-sm" onClick={() => handleAiAction('tighten')} disabled={!!aiActionLoading}>
                {aiActionLoading === 'tighten' ? '...' : 'Tighten'}
              </button>
              <button className="fa-btn fa-btn-sm" onClick={() => handleAiAction('expand')} disabled={!!aiActionLoading}>
                {aiActionLoading === 'expand' ? '...' : 'Expand'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Department Notes Section */}
      {deptNoteEntries.length > 0 && (
        <div className="fa-exec-dept-notes">
          <div
            className="fa-exec-dept-notes-header"
            onClick={() => setShowDeptNotes(!showDeptNotes)}
          >
            <span>Department Notes ({totalDeptNotes})</span>
            <svg
              width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              style={{ transform: showDeptNotes ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </div>
          {showDeptNotes && (
            <div className="fa-exec-dept-notes-list">
              {deptNoteEntries.map(([dept, notes]) => (
                <div key={dept} className="fa-exec-dept-group">
                  <div className="fa-exec-dept-name">{dept.replace(/_/g, ' ')}</div>
                  {notes.map((note) => (
                    <div key={note.id} className="fa-exec-dept-note">
                      <span className="fa-exec-dept-note-author">{note.authorId?.split('@')[0]}</span>
                      <span>{note.noteText}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExecutiveSummaryPanel;
